import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Alert, Stack } from '@mui/material';
import { Button, Dropzone } from '../../components/shared';
import { useUplodeSeedliveFileCashMutation } from '../../services/Seedlive/SeedliveService';

const REPORT_TYPE = 'Cash Report';
const EXPECTED_COLUMN_COUNT = 13;

const UploadFiles = () => {
  const [file, setFile] = useState([]);
  const [fileColumnCount, setFileColumnCount] = useState(null);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [dropZoneKey, setDropZoneKey] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const [uplodeSeedliveFileCash, { isLoading: isLoadingCash, isSuccess: isSuccessCash, reset: resetCash }] =
    useUplodeSeedliveFileCashMutation();

  const isUploading = isLoadingCash;
  const MAX_FILE_SIZE = 15 * 1024 * 1024;

  useEffect(() => {
    if (isSuccessCash) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        handleCancel();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccessCash]);

  const parseCsvSample = (text, maxSampleLines = 10) => {
    if (!text) {
      return {
        columnCount: null,
        headerColumns: null,
        dataColumns: null,
        warnings: ['File is empty.'],
      };
    }

    if (text.charCodeAt(0) === 0xfeff) {
      text = text.slice(1);
    }

    const rawLines = text
      .split(/\r?\n/)
      .map((l) => l.replace(/\r$/, ''))
      .filter((l) => l.trim().length > 0);

    const lines = rawLines.slice(0, maxSampleLines);

    const parseLine = (line) => {
      const cols = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          cols.push(current);
          current = '';
        } else {
          current += ch;
        }
      }
      cols.push(current);
      return cols;
    };

    const parsed = lines.map((l) => ({ line: l, cols: parseLine(l) }));
    if (!parsed.length) {
      return {
        columnCount: null,
        headerColumns: null,
        dataColumns: null,
        warnings: ['File appears to be empty.'],
      };
    }

    const header = parsed[0];
    let firstData = null;
    for (let i = 1; i < parsed.length; i++) {
      if (parsed[i].cols.some((c) => c.trim() !== '')) {
        firstData = parsed[i];
        break;
      }
    }

    const headerCount = header.cols.length;
    const dataCount = firstData ? firstData.cols.length : headerCount;

    const warnings = [];
    if (firstData && dataCount !== headerCount) {
      warnings.push(
        `Header has ${headerCount} columns but first data line has ${dataCount}. Using data line count for validation.`,
      );
    }

    return {
      columnCount: dataCount,
      headerColumns: header.cols,
      dataColumns: firstData ? firstData.cols : header.cols,
      warnings,
    };
  };

  const validateColumns = (count) => {
    if (count == null) return '';
    if (count !== EXPECTED_COLUMN_COUNT)
      return `Invalid file: expected ${EXPECTED_COLUMN_COUNT} columns for ${REPORT_TYPE}, found ${count}.`;
    return '';
  };

  const handleFileChange = (files) => {
    setFile(files);
    setFileColumnCount(null);
    setWarnings([]);
    if (!files || !files.length) {
      setError('');
      return;
    }
    const f = files[0];

    if (f.size > MAX_FILE_SIZE) {
      setError('File is too large. Maximum allowed size is 15 MB.');
      return;
    }
    if (f && f.type !== 'text/csv' && !f.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a .csv file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let text = e.target.result || '';
        const meta = parseCsvSample(text, 10);
        setFileColumnCount(meta.columnCount);
        setWarnings(meta.warnings);
        if (meta.columnCount == null) {
          setError('Unable to determine column count (empty file?).');
        } else {
          const msg = validateColumns(meta.columnCount);
          setError(msg || '');
        }
      } catch {
        setError('Could not read the CSV file.');
      }
    };
    const CHUNK_SIZE = 128 * 1024;
    reader.readAsText(f.slice(0, CHUNK_SIZE));
    setError('');
  };

  useEffect(() => {
    if (fileColumnCount != null) {
      const msg = validateColumns(fileColumnCount);
      setError(msg);
    }
  }, [fileColumnCount]);

  const handleUpload = async () => {
    if (!file.length) {
      setError('A file is required.');
      return;
    }
    if (fileColumnCount == null) {
      setError('Unable to determine column count.');
      return;
    }
    const msg = validateColumns(fileColumnCount);
    if (msg) {
      setError(msg);
      return;
    }

    const formData = new FormData();
    formData.append('csv_file', file[0]);
    formData.append('report_type', REPORT_TYPE);
    formData.append('JOB_TYPE', 'MANUAL');

    try {
      await uplodeSeedliveFileCash(formData).unwrap();
    } catch (err) {
      setError(err?.data?.message || 'An error occurred while uploading the file.');
    }
  };

  const handleCancel = () => {
    setFile([]);
    setError('');
    setWarnings([]);
    setFileColumnCount(null);
    setDropZoneKey((k) => k + 1);
    resetCash?.();
    setShowSuccess(false);
  };

  return (
    <React.Fragment>
      <Typography variant="h1" mb={4}>
        Upload File
      </Typography>
      <Box
        p={4}
        sx={{
          maxWidth: '800px',
          margin: '0 auto',
          border: '1px solid #ccc',
          borderRadius: 2,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="body1">Attach File:</Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Dropzone
              acceptedFiles={['.csv']}
              key={dropZoneKey}
              maxFileSize={Number.MAX_SAFE_INTEGER}
              onChange={handleFileChange}
              filesLimit={1}
            />
            <Grid item xs={12} mt={1}>
              <Alert severity="info">The maximum allowed file size is 15MB</Alert>
              <Alert severity="info">Only files with the following formats are allowed: CSV</Alert>
            </Grid>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {warnings.length > 0 && (
            <Grid item xs={12}>
              {warnings.map((w, i) => (
                <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                  {w}
                </Alert>
              ))}
            </Grid>
          )}

          {showSuccess && (
            <Grid item xs={12}>
              <Alert severity="success">File uploaded successfully!</Alert>
            </Grid>
          )}
        </Grid>

        <Stack direction="row" spacing={2} mt={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={isUploading || !!error || !file.length || fileColumnCount == null}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
          <Button variant="outlined" color="error" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      </Box>
    </React.Fragment>
  );
};

export default UploadFiles;
